import type { MemorySnapshot } from './api/core';

export type ChunkEndInfo = {
  found: true;
  page: string;
  part: string;
  next: string;
  status: string;
  raw: string;
} | { found: false };

export type ChunkStartInfo = {
  found: true;
  page: string;
  part: string;
  after: string;
  raw: string;
} | { found: false };

export type AppendResult = {
  ok: true;
  combinedText: string;
  isFinal: boolean;
  message: string;
} | {
  ok: false;
  combinedText: string;
  error: string;
};

export type ValidationResult = {
  ok: boolean;
  errors: string[];
};

export function extractHtmlCodeBlock(responseText: string): string {
  const mdFenceMatch = responseText.match(/```html\s*\n([\s\S]*?)\n\s*```/);
  if (mdFenceMatch) {
    let code = mdFenceMatch[1];
    code = code.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    code = code.replace(/^\n+/, '').replace(/\n+$/, '');
    return code;
  }

  const anyFenceMatch = responseText.match(/```\s*\n([\s\S]*?)\n\s*```/);
  if (anyFenceMatch) {
    let code = anyFenceMatch[1];
    code = code.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    code = code.replace(/^\n+/, '').replace(/\n+$/, '');
    return code;
  }

  let code = responseText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  code = code.replace(/^\n+/, '').replace(/\n+$/, '');
  return code;
}

export function parseLastChunkEnd(currentText: string): ChunkEndInfo {
  const regex = /<!--\s*AI_CHUNK_END\s+([^>]+?)\s*-->/g;
  let lastMatch: RegExpExecArray | null = null;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(currentText)) !== null) {
    lastMatch = match;
  }

  if (!lastMatch) return { found: false };

  const attrs = lastMatch[1];
  const page = attrs.match(/page=(\S+)/)?.[1] || '';
  const part = attrs.match(/part=(\d+)/)?.[1] || '';
  const next = attrs.match(/next=(\d+)/)?.[1] || '';
  const status = attrs.match(/status=(\S+)/)?.[1] || '';

  return {
    found: true,
    page,
    part,
    next,
    status,
    raw: lastMatch[0],
  };
}

export function parseChunkStart(newText: string): ChunkStartInfo {
  const match = newText.match(/<!--\s*AI_CHUNK_START\s+([^>]+?)\s*-->/);
  if (!match) return { found: false };

  const attrs = match[1];
  const page = attrs.match(/page=(\S+)/)?.[1] || '';
  const part = attrs.match(/part=(\d+)/)?.[1] || '';
  const after = attrs.match(/after=(\d+)/)?.[1] || '';

  return {
    found: true,
    page,
    part,
    after,
    raw: match[0],
  };
}

export function canAppendByAnchor(oldEnd: ChunkEndInfo, newStart: ChunkStartInfo): { ok: true } | { ok: false; error: string } {
  if (!oldEnd.found || !newStart.found) {
    return { ok: false, error: '锚点信息不完整' };
  }

  if (oldEnd.page !== newStart.page) {
    return { ok: false, error: `分块页码不一致: 期望 page=${oldEnd.page}, 收到 page=${newStart.page}` };
  }
  if (oldEnd.next !== newStart.part) {
    return { ok: false, error: `期望 part=${oldEnd.next}, 但收到 part=${newStart.part}` };
  }
  if (oldEnd.part !== newStart.after) {
    return { ok: false, error: `锚点 after 不匹配: 旧 part=${oldEnd.part}, 新 after=${newStart.after}` };
  }
  if (oldEnd.status !== 'continue') {
    return { ok: false, error: `旧锚点状态不是 continue, 而是 ${oldEnd.status}` };
  }

  return { ok: true };
}

/**
 * 剥离 AI_CHUNK_RECOVERY_START 恢复标记和 ```html 代码围栏
 * 多轮返回时下一轮开头可能带着恢复标记，需要先删掉再拼接
 */
export function stripRecoveryMarker(text: string): string {
  let cleaned = text;
  // 剥离开头的 ```html 围栏
  cleaned = cleaned.replace(/^```html\s*\n?/i, '');
  // 剥离结尾的 ``` 围栏
  cleaned = cleaned.replace(/\n?```\s*$/i, '');
  // 剥离 AI_CHUNK_RECOVERY_START 注释（及其后的换行）
  cleaned = cleaned.replace(/<!--\s*AI_CHUNK_RECOVERY_START\b[^>]*\s*-->\s*\n?/g, '');
  return cleaned;
}

/**
 * 合并多个连续的 ```html 代码围栏为一个
 *
 * 多轮流式返回时，每轮可能都带 ```html ... ``` 围栏，
 * 累积后内容会包含多个围栏对，导致渲染为多个 <pre><code> 块。
 * 本函数删除中间的围栏对（``` 闭合 + ```html 开放），只保留最外层一对围栏。
 *
 * 示例：
 * 输入：
 * ```html
 * part1
 * ```
 * ```html
 * part2
 * ```
 *
 * 输出：
 * ```html
 * part1
 * part2
 * ```
 *
 * 安全性：
 * - 只匹配 ``` + 纯空白 + ```html 的模式（中间无其他文本）
 * - 不会合并中间有解释文字的代码块（如 "好的，继续..."）
 * - 不会合并不同语言的代码块（如 ```js）
 */
/**
 * 合并多段 HTML 代码围栏，确保渲染为单个 <pre><code> 块
 * 处理场景：
 * 1. 剥离 AI_CHUNK 相关注释（RECOVERY_START, START, END, FINAL_END）
 * 2. 合并标准围栏对：``` + 空白 + ```html → 换行
 * 3. 删除"不在行首"的 ```html（恢复标记围栏，前面有非空白字符）
 * 4. 截断场景：第一段无结尾 ```，后续 ```html 需删除（只保留第一个在行首的 ```html）
 * 5. 删除中间多余的闭合围栏 ```，只保留最后一个
 */
export function mergeHtmlFences(content: string): string {
  let result = content;

  // 1. 剥离所有 AI_CHUNK 相关注释（RECOVERY_START, START, END, FINAL_END）
  result = result.replace(/<!--\s*AI_CHUNK_\w+\b[^>]*\s*-->\s*\n?/g, '');

  // 2. 合并标准围栏对：``` + 空白 + ```html → 换行
  result = result.replace(/```\s*\n?\s*```html\s*\n?/g, '\n');

  // 3. 删除"不在行首"的 ```html（恢复标记围栏，前面有非空白字符）
  // 场景：第一段代码在行中间被截断（如 CSS 属性 margin-top:），第二段开头带 ```html 恢复标记
  // 例如：margin-top:```html \n 20px; → margin-top:\n 20px;
  // 注意：行首的 ```html（前面是 \n 或字符串开头）不会被删除，由步骤 4 处理
  result = result.replace(/(\S)```html\s*\n?/g, '$1\n');

  // 4. 截断场景：第一段无结尾 ```，后续 ```html 需要删除
  // 只保留第一个"在行首"的 ```html，其余替换为换行
  const firstHtmlFenceIdx = result.indexOf('```html');
  if (firstHtmlFenceIdx !== -1) {
    const before = result.substring(0, firstHtmlFenceIdx + 8); // 包含第一个 ```html
    const after = result.substring(firstHtmlFenceIdx + 8).replace(/```html\s*\n?/g, '\n');
    result = before + after;
  }

  // 5. 删除中间多余的闭合围栏 ```（独占一行的），只保留最后一个
  const closingMatches = [...result.matchAll(/^```\s*$/gm)];
  if (closingMatches.length > 1) {
    const lastMatch = closingMatches[closingMatches.length - 1];
    const lastIdx = lastMatch.index!;
    const before = result.substring(0, lastIdx).replace(/^```\s*$/gm, '');
    const after = result.substring(lastIdx);
    result = before + after;
  }

  return result;
}

/**
 * 判断单行是否完整（isLastLineIncomplete 的反向逻辑 + 增强）
 * 用于智能行拼接时判断旧末行是否需要续接/替换
 */
export function isLineComplete(line: string): boolean {
  const trimmed = line.trimEnd();
  if (!trimmed) return true; // 空行视为完整

  // 未闭合标签（任何 <tag 未以 > 结尾）
  if (/<[a-zA-Z][^>]*$/.test(trimmed)) return false;

  // 未闭合引号
  const singleQuotes = (trimmed.match(/'/g) || []).length;
  const doubleQuotes = (trimmed.match(/"/g) || []).length;
  if (singleQuotes % 2 !== 0 || doubleQuotes % 2 !== 0) return false;

  // 行尾是操作符/分隔符（表示行未结束）
  if (/[.,+:=([{\]\\]$/.test(trimmed)) return false;

  // 未闭合函数声明
  if (/^\s*(function\s+\w*\s*\(|const\s+\w+\s*=\s*\(|let\s+\w+\s*=\s*\(|var\s+\w+\s*=\s*\()/.test(trimmed) && !trimmed.endsWith('}')) return false;

  // 未闭合 url()
  if (/url\([^)]*$/.test(trimmed)) return false;

  return true;
}

/** 辅助函数：计算两个字符串的公共前缀长度 */
function commonPrefixLength(a: string, b: string): number {
  const minLen = Math.min(a.length, b.length);
  let i = 0;
  while (i < minLen && a[i] === b[i]) i++;
  return i;
}

export type SmartAppendMode = 'continue' | 'newline' | 'replace';

export type SmartAppendResult = { text: string; mode: SmartAppendMode };

/**
 * 智能行拼接：自动判断下一轮开头与上一轮结尾的连接方式
 *
 * 三种模式：
 * - continue: 接续同一行（旧末行不完整 + 新首行不像新元素开头 → 不加 \n 直接拼接）
 * - newline: 换新行（旧末行完整 → 加 \n）
 * - replace: 替换最后一行（旧末行不完整 + 新首行与旧末行有足够长公共前缀 → 删除旧末行再加新行）
 */
export function smartAppendLine(oldText: string, newText: string): SmartAppendResult {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  const oldLastLine = oldLines[oldLines.length - 1];
  const newFirstLine = newLines[0];

  // 空行 → 新行
  if (!oldLastLine.trim()) {
    return { text: oldText + '\n' + newText, mode: 'newline' };
  }

  // 旧末行完整 → 新行
  if (isLineComplete(oldLastLine)) {
    return { text: oldText + '\n' + newText, mode: 'newline' };
  }

  // 旧末行不完整
  // 检测"替换最后一行"：新首行与旧末行有足够长的公共前缀
  const oldTrimmed = oldLastLine.trim();
  const newTrimmed = newFirstLine.trim();
  const prefixLen = commonPrefixLength(oldTrimmed, newTrimmed);
  if (prefixLen >= 5 && prefixLen >= oldTrimmed.length * 0.5) {
    // 新首行是旧末行的完整版本 → 替换
    const oldWithoutLast = oldLines.slice(0, -1).join('\n');
    return { text: oldWithoutLast + '\n' + newText, mode: 'replace' };
  }

  // 检测"接续同一行"：新首行不像新元素的开头
  const newStarts = newFirstLine.trimStart();
  const startsNewElement =
    newStarts.startsWith('<') ||
    newStarts.startsWith('//') ||
    newStarts.startsWith('/*') ||
    newStarts.startsWith('@') ||
    newStarts.startsWith('{') ||
    newStarts.startsWith('}');
  if (!startsNewElement) {
    // 新首行是旧末行的续接 → 同一行
    return { text: oldText + newText, mode: 'continue' };
  }

  // 默认 → 新行
  return { text: oldText + '\n' + newText, mode: 'newline' };
}

export function appendWithLineOverlap(oldText: string, newText: string): string {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');

  const maxCheck = 30;
  const tailOld = oldLines.slice(-maxCheck);
  const headNew = newLines.slice(0, maxCheck);

  let bestOverlapLen = 0;
  for (let len = 1; len <= Math.min(tailOld.length, headNew.length); len++) {
    const oldSlice = tailOld.slice(-len);
    const newSlice = headNew.slice(0, len);
    let allMatch = true;
    for (let i = 0; i < len; i++) {
      if (oldSlice[i] !== newSlice[i]) {
        allMatch = false;
        break;
      }
    }
    if (allMatch) {
      bestOverlapLen = len;
    }
  }

  if (bestOverlapLen > 50) {
    console.warn(`重复行数 ${bestOverlapLen} 超过 50 行，可能 AI 重输了大段代码，拒绝拼接`);
    return oldText;
  }

  if (bestOverlapLen > 0) {
    const trimmedNewLines = newLines.slice(bestOverlapLen);
    return oldLines.concat(trimmedNewLines).join('\n');
  }

  // 无重叠 → 智能行拼接（替代原来的 oldText + '\n' + newText）
  return smartAppendLine(oldText, newText).text;
}

function isLastLineIncomplete(text: string): boolean {
  const lines = text.split('\n');
  const lastLine = lines[lines.length - 1].trimEnd();
  if (!lastLine) return false;

  if (/<div[^>]*$/.test(lastLine)) return true;
  if (/<span[^>]*$/.test(lastLine)) return true;
  if (/[.,+:=([{\]\\]$/.test(lastLine)) return true;

  const singleQuotes = (lastLine.match(/'/g) || []).length;
  const doubleQuotes = (lastLine.match(/"/g) || []).length;
  if (singleQuotes % 2 !== 0 || doubleQuotes % 2 !== 0) return true;

  if (/^\s*(function\s+\w*\s*\(|const\s+\w+\s*=\s*\(|let\s+\w+\s*=\s*\(|var\s+\w+\s*=\s*\()/.test(lastLine) && !lastLine.endsWith('}')) return true;

  if (/url\([^)]*$/.test(lastLine)) return true;

  return false;
}

export function appendHtmlChunk(currentText: string, newChunkText: string): AppendResult {
  // 0. 剥离恢复标记和代码围栏（AI_CHUNK_RECOVERY_START + ```html 围栏）
  newChunkText = stripRecoveryMarker(newChunkText);

  // 1. 编辑框为空 → 直接使用新片段
  if (!currentText.trim()) {
    return {
      ok: true,
      combinedText: newChunkText,
      isFinal: isFinalChunk(newChunkText),
      message: '编辑框为空，直接使用新片段',
    };
  }

  // 2. 当前已标记最终完成 → 拒绝追加
  if (/<!--\s*AI_CHUNK_FINAL_END\b/.test(currentText)) {
    return {
      ok: false,
      combinedText: currentText,
      error: '当前 HTML 已标记为完成(AI_CHUNK_FINAL_END)，不允许继续追加',
    };
  }

  const newStart = parseChunkStart(newChunkText);
  const newHasFinalEnd = isFinalChunk(newChunkText);
  const lastEnd = parseLastChunkEnd(currentText);

  // 3. 新片段是完整 HTML 且无锚点 → 直接替换（关键修复！）
  if (/<!DOCTYPE\s+html/i.test(newChunkText.trim()) && /<html/i.test(newChunkText.trim())) {
    if (!newStart.found && newHasFinalEnd) {
      return {
        ok: true,
        combinedText: newChunkText,
        isFinal: true,
        message: '新片段为完整 HTML，已直接替换',
      };
    }
    if (!newStart.found) {
      return {
        ok: false,
        combinedText: currentText,
        error: '新片段像是从头重新生成(包含<!DOCTYPE html>)，不是续写。请重新输出当前分块。',
      };
    }
  }

  // 4. 当前内容已完整（有 </html> 且无 continue）→ 拒绝
  const hasClosingHtml = currentText.includes('</html>');
  const hasContinueEnd = /<!--\s*AI_CHUNK_END\b[^>]*\bstatus=continue\b/.test(currentText);
  if (hasClosingHtml && !hasContinueEnd) {
    return {
      ok: false,
      combinedText: currentText,
      error: '当前 HTML 已包含 </html> 且无 continue 标记，已完整。如需重新生成，请清空编辑框后重试。',
    };
  }

  // 5. 期待续写（旧有 continue 锚点）→ 必须校验锚点
  if (lastEnd.found && lastEnd.status === 'continue') {
    if (!newStart.found) {
      return {
        ok: false,
        combinedText: currentText,
        error: '当前编辑框期待续写(AI_CHUNK_END status=continue)，但新片段缺少 AI_CHUNK_START 锚点',
      };
    }

    const check = canAppendByAnchor(lastEnd, newStart);
    if (!check.ok) {
      return {
        ok: false,
        combinedText: currentText,
        error: check.error,
      };
    }

    const combined = appendWithLineOverlap(currentText.trimEnd(), newChunkText.trimStart());
    return {
      ok: true,
      combinedText: combined,
      isFinal: isFinalChunk(combined),
      message: '锚点校验通过，拼接成功',
    };
  }

  // 6. 双方都无锚点 → 兼容模式智能拼接
  if (!lastEnd.found && !newStart.found) {
    // 不再拒绝 isLastLineIncomplete，改用 smartAppendLine 智能拼接
    const combined = appendWithLineOverlap(currentText.trimEnd(), newChunkText.trimStart());
    return {
      ok: true,
      combinedText: combined,
      isFinal: isFinalChunk(combined),
      message: '无锚点兼容模式：智能行拼接',
    };
  }

  // 7. 旧内容无锚点，新片段有锚点 → 兼容追加
  if (!lastEnd.found && newStart.found) {
    const combined = appendWithLineOverlap(currentText.trimEnd(), newChunkText.trimStart());
    return {
      ok: true,
      combinedText: combined,
      isFinal: isFinalChunk(combined),
      message: '旧内容无锚点但新片段有锚点，兼容追加',
    };
  }

  return {
    ok: false,
    combinedText: currentText,
    error: '无法确定拼接方式',
  };
}

export function isFinalChunk(text: string): boolean {
  const hasFinalEnd = /<!--\s*AI_CHUNK_FINAL_END\b/.test(text);
  const hasClosingHtml = text.includes('</html>');
  const hasContinueEnd = /<!--\s*AI_CHUNK_END\b[^>]*\bstatus=continue\b/.test(text);

  if (hasFinalEnd && hasClosingHtml) return true;
  if (!hasContinueEnd && hasClosingHtml) return true;
  return false;
}

export function cleanChunkAnchorsForFinalHtml(text: string): string {
  let cleaned = text;
  cleaned = cleaned.replace(/<!--\s*AI_CHUNK_START\b[^>]*\s*-->\s*\n?/g, '');
  cleaned = cleaned.replace(/<!--\s*AI_CHUNK_END\b[^>]*\s*-->\s*\n?/g, '');
  cleaned = cleaned.replace(/<!--\s*AI_CHUNK_FINAL_END\b[^>]*\s*-->\s*\n?/g, '');
  cleaned = cleaned.replace(/<!--\s*AI_OVERLAP_CHECK\b[^>]*\s*-->\s*\n?/g, '');
  cleaned = cleaned.replace(/<!--\s*AI_CHUNK_RECOVERY_START\b[^>]*\s*-->\s*\n?/g, '');
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  return cleaned.trim() + '\n';
}

export function validateFinalHtml(html: string): ValidationResult {
  const errors: string[] = [];

  // Structural checks only (standard HTML structure)
  if (!html.includes('<!DOCTYPE html>')) errors.push('缺少 <!DOCTYPE html>');
  if (!/<html[\s>]/i.test(html)) errors.push('缺少 <html');
  if (!html.includes('</html>')) errors.push('缺少 </html>');
  if (!/<head[\s>]/i.test(html)) errors.push('缺少 <head');
  if (!html.includes('</head>')) errors.push('缺少 </head>');
  if (!/<body[\s>]/i.test(html)) errors.push('缺少 <body');
  if (!html.includes('</body>')) errors.push('缺少 </body>');
  if (!/<style[\s>]/i.test(html)) errors.push('缺少 <style');
  if (!html.includes('</style>')) errors.push('缺少 </style>');
  if (!/<script[\s>]/i.test(html)) errors.push('缺少 <script');
  if (!html.includes('</script>')) errors.push('缺少 </script>');

  // Markdown/code fence checks
  if (html.includes('```html')) errors.push('不得包含 ```html');
  if (/^```$/m.test(html)) errors.push('不得包含 ```');
  if (/AI_CHUNK_START/.test(html)) errors.push('不得包含 AI_CHUNK_START');
  if (/AI_CHUNK_END/.test(html)) errors.push('不得包含 AI_CHUNK_END');
  if (/AI_CHUNK_FINAL_END/.test(html)) errors.push('不得包含 AI_CHUNK_FINAL_END');
  if (/status=continue/.test(html)) errors.push('不得包含 status=continue');

  return {
    ok: errors.length === 0,
    errors,
  };
}

export function buildContinueChunkPrompt(currentHtml: string): string {
  const lastEnd = parseLastChunkEnd(currentHtml);

  if (!lastEnd.found) {
    return `继续仅输出剩余代码: 不要再从开头<!DOCTYPE html><html>开始生成！回看前一条消息你刚生成过的前半部分网页代码最后一行，接着生成网页剩余代码即可！`;
  }

  const { page, part, next } = lastEnd;
  const nextPlusOne = String(Number(next) + 1).padStart(2, '0');

  return `继续输出当前 HTML 的下一安全分块。

上一段结束锚点如下：
${lastEnd.raw}

请严格执行：
1. 只输出一个 \`\`\`html 代码块，不要输出任何解释文字。
2. 本次代码块第一行必须是：
<!-- AI_CHUNK_START page=${page} type=html part=${next} after=${part} mode=chunked -->
3. 只输出 part=${next} 的新增代码。
4. 不要从 <!DOCTYPE html>、<html>、<head> 重新开始。
5. 不要重复上一段代码。
6. 不要改写上一段最后一行。
7. 不要接续半截行。
8. 必须从上一段 AI_CHUNK_END 锚点之后的新内容开始。
9. 如果本段仍未完成，停在完整行，并以：
<!-- AI_CHUNK_END page=${page} type=html part=${next} next=${nextPlusOne} status=continue stop=safe_line -->
结束。
10. 如果本段已完成，必须包含 </html>，并以：
<!-- AI_CHUNK_FINAL_END page=${page} type=html parts=${next} status=complete -->
结束。`;
}

/**
 * 从 AI 响应文本中提取"单页教研记忆快照"JSON 块
 * 识别特征：```json 代码块 + 包含 main_goal 或 microcheck 字段
 * 返回 null 表示未找到或解析失败
 */
export function extractMemorySnapshot(content: string): MemorySnapshot | null {
  if (!content) return null;
  // 匹配 ```json 代码块：先尝试标准闭合块，再回退到未闭合（截断）场景
  const jsonBlockMatch = content.match(/```json\s*\n([\s\S]*?)\n\s*```/)
    || content.match(/```json\s*\n([\s\S]*?)$/);
  if (!jsonBlockMatch) return null;

  try {
    const parsed = JSON.parse(jsonBlockMatch[1]);
    if (!parsed || typeof parsed !== 'object') return null;
    // 特征字段校验：至少包含 main_goal 或 microcheck
    if (!('main_goal' in parsed) && !('microcheck' in parsed)) return null;

    return {
      page: String(parsed.page ?? ''),
      segment: String(parsed.segment ?? ''),
      page_mode: String(parsed.page_mode ?? ''),
      theme_mode: String(parsed.theme_mode ?? ''),
      tool_state: String(parsed.tool_state ?? ''),
      main_goal: String(parsed.main_goal ?? ''),
      teacher_signal: String(parsed.teacher_signal ?? ''),
      student_first_action: String(parsed.student_first_action ?? ''),
      microcheck: String(parsed.microcheck ?? ''),
      handoff_hook: String(parsed.handoff_hook ?? ''),
      media_decision: String(parsed.media_decision ?? ''),
    };
  } catch {
    return null;
  }
}
